/**
 * Calendar month hook (replacement data layer).
 */
import { useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useAuthStore } from '../../../shared/auth/store';
import { useSubscription } from '../subscription/hooks';
import { policies } from '../../query/policies';
import { fetchCalendarMonth } from './api';
import { deriveCalendarMonthFromApi } from './api';
import { calendarKeys } from './keys';

export function useCalendarMonth(month?: string) {
  const customer_id = useAuthStore(s => s.user?.id);
  const { availableDates, refetch: refetchSubscription } = useSubscription();

  const month_key = month ?? '';
  const enabled = typeof customer_id === 'number' && month_key.length >= 7;

  const { data: query_data, isLoading, refetch: refetch_calendar_month } = useQuery({
    queryKey: calendarKeys.month(customer_id ?? 0, month_key),
    queryFn: () => fetchCalendarMonth(customer_id!, month_key),
    enabled,
    staleTime: policies.calendar.staleTime,
    gcTime: policies.calendar.gcTime,
    refetchOnWindowFocus: false,
  });

  const api_data = query_data?.data ?? null;
  const derived = useMemo(() => deriveCalendarMonthFromApi(api_data ?? undefined), [api_data]);
  const { stateByDate: state_map, iconByDate: icon_map, frozenDates: api_frozen_dates } = derived;
  const available_set = useMemo(() => new Set(availableDates), [availableDates]);

  const getDateState = useCallback(
    (date: string): string | undefined => {
      const from_api = state_map.get(date);
      if (from_api) {return from_api;}
      if (available_set.has(date)) {return 'Select Meal';}
      return undefined;
    },
    [state_map, available_set],
  );

  const getDateIconFromApi = useCallback((date: string) => icon_map.get(date), [icon_map]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchSubscription(), refetch_calendar_month()]);
  }, [refetchSubscription, refetch_calendar_month]);

  return useMemo(
    () => ({
      data: api_data,
      isLoading,
      refetch,
      getDateState,
      getDateIconFromApi,
      apiFrozenDates: api_frozen_dates,
    }),
    [api_data, isLoading, refetch, getDateState, getDateIconFromApi, api_frozen_dates],
  );
}

export function useCalendarWarmup(enabled: boolean): void {
  const customer_id = useAuthStore(s => s.user?.id);
  const query_client = useQueryClient();
  const { isLoading: subscription_loading } = useSubscription();

  useEffect(() => {
if (!enabled || subscription_loading || typeof customer_id !== 'number') {
      return;
    }
    const months = [moment().format('YYYY-MM'), moment().add(1, 'month').format('YYYY-MM')];
    for (const month_ym of months) {
      void query_client.prefetchQuery({
        queryKey: calendarKeys.month(customer_id, month_ym),
        queryFn: () => fetchCalendarMonth(customer_id, month_ym),
        staleTime: policies.calendar.staleTime,
        gcTime: policies.calendar.gcTime,
      });
    }
  }, [enabled, subscription_loading, customer_id, query_client]);
}

export function usePrefetchDashboardCalendarMonths(enabled: boolean): void {
  useCalendarWarmup(enabled);
}

export { useCalendarMonth as useCalendarData, useCalendarWarmup as usePrefetchNearbyCalendarMonths };
